import React, { useState } from 'react';
import { ServiceData } from '../../redux/services/ServiceRedux';
import { connect, WithStore, MapStateToProps, MapDispatchToProps } from '../../redux/services/Imports';
import { ValidationFormItems } from '../../services/GlobalFunctions';
import { useSmartCaptcha } from '../SmartCaptcha/useSmartCaptcha';
import SmartCaptchaBox from '../SmartCaptcha/SmartCaptchaBox';
import { documentUrl } from '../../services/documents';
import { Props } from './interfaces';

/**
 * Горизонтальная мини-форма (блок next_day_form).
 *
 * Перенесена из сниппета Code Snippets. В оригинале форма слала заявку своим
 * обработчиком admin-ajax и wp_mail на админский адрес — то есть мимо
 * Contact Form 7, а значит мимо интеграции с amoCRM и мимо капчи. На проде так
 * нельзя: лиды бы просто не доезжали до отдела продаж. Поэтому разметка и
 * классы оставлены прежними (стили переехали в style.css как есть), а отправка
 * переведена на общий путь: SmartCaptcha → CF7 → amoCRM, как у остальных форм.
 */
const FormMiniHorizontal = ({
  buttonText,
  contactFormId,
  contactForms,
  eventMetrika,
  blockedSend,
  blockedSendLoaded,
}: Props) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(true);

  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
  const [serverText, setServerText] = useState<string | null>(null);
  const [successShow, setSuccessShow] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false);

  const captcha = useSmartCaptcha(contactForms);

  const validate = (): boolean => {
    const next: { [key: string]: string | null } = {};

    if (!phone.trim()) {
      next.phone = 'Укажите телефон';
    } else if (!ValidationFormItems({ phone, setErrorPhone: (err: string | null) => { next.phone = err; } })) {
      next.phone = next.phone || 'Проверьте номер телефона';
    }

    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = 'Неверный формат email';
    }

    if (!consent) {
      next.consent = 'Нужно согласие на обработку данных';
    }

    setErrors(next);

    return Object.values(next).every((v) => !v);
  };

  const success = () => {
    setName('');
    setPhone('');
    setEmail('');
    setConsent(false);
    captcha.reset();
    setSuccessShow(true);
    setTimeout(() => setSuccessShow(false), 4000);
    setDisableBtn(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerText(null);
    setDisableBtn(true);

    if (blockedSend.state) {
      setDisableBtn(false);
      return;
    }

    if (!validate()) {
      setDisableBtn(false);
      return;
    }

    let token = '';
    try {
      token = await captcha.ensureToken();
    } catch (err) {
      console.error('[mini-form] отправка отменена: капча не выдала токен', {
        reason: (err as any)?.message ?? String(err),
      });
      setDisableBtn(false);
      return;
    }

    ServiceData.submitForm({
      data: { name, phone, email, allow_marketing: marketing ? '1' : '' },
      token,
      setServerText: (t: string) => { setServerText(t); captcha.reset(); setDisableBtn(false); },
      success,
      blockedSendLoaded,
      eventMetrika: eventMetrika || 'next_day_mini_form',
      contactFormId,
    });
  };

  // Заголовок и иконку рендерит PHP-шаблон блока: они статичные и являются
  // частью сетки .dalli-mini__panel. React отвечает только за саму форму.
  return (
    <form className="dalli-mini__form" onSubmit={handleSubmit} noValidate>
      <div className="dalli-mini__field" data-field="name">
        <input
          className="dalli-mini__input"
          name="name"
          type="text"
          autoComplete="name"
          placeholder="Ваше имя"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="dalli-mini__field" data-field="phone">
        <input
          className="dalli-mini__input"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+7 (000) 000-00-00"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        {errors.phone ? <span className="dalli-mini__error">{errors.phone}</span> : null}
      </div>

      <div className="dalli-mini__field" data-field="email">
        <input
          className="dalli-mini__input"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="E-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {errors.email ? <span className="dalli-mini__error">{errors.email}</span> : null}
      </div>

      <button className="dalli-mini__submit" type="submit" disabled={disableBtn}>
        <span className="dalli-mini__submit-default">{buttonText || 'Получить расчет'}</span>
      </button>

      {/* Обёртка обязательна: в вёрстке у неё grid-column: 1 / -1,
          без неё согласия сжимаются в первую колонку сетки формы. */}
      <div className="dalli-mini__agreements">
        <div className="dalli-mini__consent-wrap" data-field="consent">
          <label className="dalli-mini__consent">
            <input
              type="checkbox"
              name="consent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span className="dalli-mini__checkbox" aria-hidden="true"></span>
            <span>
              Я соглашаюсь с <a href={documentUrl('policy')} target="_blank" rel="noopener noreferrer">политикой конфиденциальности</a> и даю согласие на обработку персональных данных
            </span>
          </label>
          {errors.consent ? <span className="dalli-mini__error">{errors.consent}</span> : null}

          <label className="dalli-mini__consent">
            <input
              type="checkbox"
              name="marketing_consent"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
            />
            <span className="dalli-mini__checkbox" aria-hidden="true"></span>
            <span>
              Я даю <a href={documentUrl('newsletter')} target="_blank" rel="noopener noreferrer">согласие на получение новостной и рекламной рассылки</a>
            </span>
          </label>
        </div>
      </div>

      <SmartCaptchaBox captcha={captcha} />

      {serverText ? <div className="dalli-mini__status dalli-mini__status--error">{serverText}</div> : null}
      {successShow ? <div className="dalli-mini__status dalli-mini__status--success">Спасибо! Мы получили вашу заявку.</div> : null}
    </form>
  );
};

// ownProps приходят из data-props в PHP и должны перекрывать значения из Redux.
const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
});

export default WithStore()(connect(MapStateToProps, MapDispatchToProps, mergeProps)(FormMiniHorizontal));
